import { Controller } from "@hotwired/stimulus"
import { paginateBillingDocument } from "../billing/document_paginator"

// Live document preview beside the billing edit forms. Posts the form's current
// (unsaved) state to the preview endpoint and drops the returned HTML into an
// iframe. The endpoint never persists anything — see Billing::DocumentPreviewable.
//
// The PDF layout has no page width of its own. The frame fills the pane at A4
// proportions and the document inside is zoomed so it still lays out as a 794px
// A4 page — keeping the preview faithful to the printed document at any pane size.
// zoom (not transform: scale) because zoom re-lays out, so text stays crisp when
// the pane is wider than A4 instead of being rasterised at 794px and stretched.
const A4_WIDTH_PX = 794 // 210mm at 96dpi
// Past this the page is just bigger than life-size and the form starts to feel
// squeezed; the page centres in the leftover space instead of growing further.
const MAX_ZOOM = 1.5
const DEBOUNCE_MS = 600

export default class extends Controller {
  static targets = ["frame", "page", "status"]
  // fields: Billing::DocumentPreviewable::PREVIEWABLE_ATTRIBUTES, rendered into the
  // pane so this controller can post — and diff — exactly what the endpoint permits.
  static values = { url: String, fields: Array }

  connect() {
    // The pane sits inside the form, so a data-action on the form could not reach
    // this controller (Stimulus resolves actions upwards, not into descendants).
    // Binding here also keeps the partial self-contained for reuse.
    this.form = this.element.closest("form")
    this.schedule = this.schedule.bind(this)
    this.form?.addEventListener("input", this.schedule)
    this.form?.addEventListener("change", this.schedule)

    this.scale()
    this.resizeObserver = new ResizeObserver(() => this.scheduleScale())
    this.resizeObserver.observe(this.element)
    this.refresh()
  }

  disconnect() {
    this.form?.removeEventListener("input", this.schedule)
    this.form?.removeEventListener("change", this.schedule)
    clearTimeout(this.timeout)
    this.abortInFlight()
    this.resizeObserver?.disconnect()
    cancelAnimationFrame(this.scaleFrame)
  }

  // ResizeObserver entry point. scale() -> fit() resizes the frame, which resizes the
  // observed pane; doing that inside the callback leaves a notification the browser
  // cannot deliver in the same frame, and Chrome reports it as "ResizeObserver loop
  // completed with undelivered notifications". Deferring to the next frame moves the
  // write out of the delivery, and only a width change can alter the zoom — the height
  // change is fit()'s own echo, so it is ignored instead of triggering another pass.
  scheduleScale() {
    cancelAnimationFrame(this.scaleFrame)
    this.scaleFrame = requestAnimationFrame(() => {
      if (!this.hasPageTarget || this.pageTarget.parentElement.clientWidth === this.scaledFor)
        return
      this.scale()
    })
  }

  // Debounced entry point, bound to input/change on the form.
  schedule() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.refresh(), DEBOUNCE_MS)
  }

  async refresh() {
    if (!this.form || !this.hasFrameTarget) return

    const body = this.previewFormData()
    // Most form events cannot change the rendered document: they touch a field the
    // endpoint discards, or re-emit the same value. Rendering costs a TreeBuilder, a
    // TaxCalculator and the whole PDF template, so only ask when something moved.
    const signature = this.signatureFor(body)
    if (signature === this.lastSignature) return

    this.abortInFlight()
    this.controller = new AbortController()
    this.setStatus("updating")

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: "text/html",
        },
        signal: this.controller.signal,
      })

      if (!response.ok) throw new Error(`Preview failed: ${response.status}`)

      const html = await response.text()
      // The pane scrolls with the page, not inside the frame — fit() sizes the iframe
      // to the whole document, so it never has a scrollbar of its own.
      const scrollTop = window.scrollY
      this.frameTarget.srcdoc = html
      // Restore the reading position so a refresh mid-document doesn't jump to
      // the top while the user is still typing further down.
      this.frameTarget.addEventListener(
        "load",
        () => {
          // Paginate before zooming. zoom is a fractional relayout snapped to 1/64px,
          // so a line can wrap differently at 1.37x than at 1x — measuring after it
          // would launder that difference into the break decision. Once the content is
          // distributed across fixed-height sheets, zoom scales the stack uniformly and
          // cannot move anything between them.
          this.paginate()
          this.scale()
          window.scrollTo(0, scrollTop)
        },
        { once: true },
      )
      // Recorded only on success, so a failed render is retried by the next edit
      // instead of being mistaken for "already showing these values".
      this.lastSignature = signature
      this.setStatus("ready")
    } catch (error) {
      if (error.name === "AbortError") return
      // Keep the last good render on screen — a blank pane is worse than a
      // slightly stale one.
      this.setStatus("failed")
    }
  }

  // Only the fields the endpoint permits. Matching on the bracketed attribute rather
  // than the full name keeps the param namespace (billing_offer / billing_invoice) out
  // of here — one list, rendered from the Ruby constant, covers both forms.
  //
  // Building the body from scratch also drops Rails' _method=patch, which Rack's method
  // override would otherwise use to rewrite this POST into a PATCH and miss the route.
  previewFormData() {
    const wanted = new Set(this.fieldsValue)
    const body = new FormData()
    for (const [name, value] of new FormData(this.form)) {
      const attribute = name.match(/\[([^[\]]+)\]$/)?.[1]
      if (attribute && wanted.has(attribute)) body.append(name, value)
    }
    return body
  }

  // Safe to stringify: the filter above keeps only text/date/number/radio fields, so a
  // value is never a File.
  signatureFor(body) {
    return [...body].map(([name, value]) => `${name}=${value}`).join("\n")
  }

  // Split the rendered flow into real A4 sheets, using the same module wkhtmltopdf runs
  // inline — so the pane breaks where the printer breaks instead of guessing.
  //
  // Called only from the load handler. The ResizeObserver must never reach it: zoom
  // scales an already-fragmented stack uniformly, so nothing can move between sheets,
  // and re-paginating there would turn the width-guarded resize -> scale() path into
  // an endless relayout loop.
  paginate() {
    const doc = this.frameTarget.contentDocument
    if (doc) paginateBillingDocument(doc)
  }

  // Match the document's zoom to however wide the pane currently is.
  scale() {
    if (!this.hasPageTarget) return

    // Measure the wrapper, not the pane: the pane's own padding is not available
    // to the page, and sizing to the pane width clips the document's right edge.
    const available = this.pageTarget.parentElement.clientWidth
    // Hidden (below xl): forget the last width, or a document reloaded while hidden would
    // skip its first scale when the pane comes back at that same width.
    if (!available) {
      this.scaledFor = null
      return
    }
    this.scaledFor = available

    const width = Math.min(available, A4_WIDTH_PX * MAX_ZOOM)
    this.factor = width / A4_WIDTH_PX
    this.pageTarget.style.width = `${width}px`
    this.applyZoom()
  }

  // Only possible because the frame is same-origin (srcdoc + allow-same-origin);
  // scripts stay blocked, so the preview cannot execute anything.
  applyZoom() {
    if (!this.factor) return

    const root = this.frameTarget.contentDocument?.documentElement
    if (root) root.style.zoom = this.factor
    this.fit()
  }

  // Grow the frame to the whole document instead of one A4 page. A two-page invoice is
  // two sheets tall and the user needs to see the second one — the pane scrolls, the
  // iframe does not. Measured with getBoundingClientRect: it reports the post-zoom box,
  // while scrollHeight is inconsistent about whether root zoom is applied.
  fit() {
    const doc = this.frameTarget.contentDocument
    // .bd-pages wraps every sheet; display: flow-root keeps the last one's bottom
    // margin inside the measured box.
    const sheet = doc?.querySelector(".bd-pages") || doc?.body
    if (!sheet) return

    const height = Math.round(sheet.getBoundingClientRect().height)
    if (height > 0) this.frameTarget.style.height = `${height}px`
  }

  abortInFlight() {
    this.controller?.abort()
    this.controller = null
  }

  setStatus(state) {
    if (!this.hasStatusTarget) return

    for (const element of this.statusTargets) {
      element.classList.toggle("hidden", element.dataset.state !== state)
    }
  }
}
